import { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import apiService from '../../services/api.service';
import './AS2Metrics.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const AS2Metrics = () => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const response = await apiService.get('/as2/metrics');
                if (response.success) {
                    setMetrics(response.data);
                } else {
                    setError('Failed to fetch metrics');
                }
            } catch (err) {
                console.error('Error fetching metrics:', err);
                setError('An error occurred while fetching metrics');
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, []);

    if (loading) return <div className="loading">Loading metrics...</div>;
    if (error) return <div className="error">{error}</div>;
    if (!metrics) return null;

    // Prepare Chart Data
    const volumeData = {
        labels: metrics.volume.map(v => v.direction),
        datasets: [{
            data: metrics.volume.map(v => v.count),
            backgroundColor: ['#6366f1', '#8b5cf6'],
            borderWidth: 0
        }]
    };

    const aggregatedStatus = metrics.status.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + parseInt(s.count);
        return acc;
    }, {});

    const statusData = {
        labels: Object.keys(aggregatedStatus),
        datasets: [{
            data: Object.values(aggregatedStatus),
            backgroundColor: [
                '#10b981', // SUCCESS
                '#ef4444', // FAILED
                '#f59e0b', // PENDING
                '#6366f1'  // OTHERS
            ],
            borderWidth: 0
        }]
    };

    const topSendersData = {
        labels: metrics.topSenders.map(s => s.name),
        datasets: [{
            label: 'Transmissions',
            data: metrics.topSenders.map(s => s.count),
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderRadius: 6
        }]
    };

    const topReceiversData = {
        labels: metrics.topReceivers.map(r => r.name),
        datasets: [{
            label: 'Transmissions',
            data: metrics.topReceivers.map(r => r.count),
            backgroundColor: 'rgba(139, 92, 246, 0.8)',
            borderRadius: 6
        }]
    };

    // Process daily volume for line chart
    const dailyLabels = [...new Set(metrics.dailyVolume.map(d => new Date(d.date).toLocaleDateString()))];
    const inboundDaily = dailyLabels.map(label => {
        const entry = metrics.dailyVolume.find(d => new Date(d.date).toLocaleDateString() === label && d.direction === 'INBOUND');
        return entry ? parseInt(entry.count) : 0;
    });
    const outboundDaily = dailyLabels.map(label => {
        const entry = metrics.dailyVolume.find(d => new Date(d.date).toLocaleDateString() === label && d.direction === 'OUTBOUND');
        return entry ? parseInt(entry.count) : 0;
    });

    const dailyData = {
        labels: dailyLabels,
        datasets: [
            {
                label: 'Inbound',
                data: inboundDaily,
                borderColor: '#6366f1',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                fill: true,
                tension: 0.4
            },
            {
                label: 'Outbound',
                data: outboundDaily,
                borderColor: '#8b5cf6',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                fill: true,
                tension: 0.4
            }
        ]
    };

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Analytics</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">AS2 Metrics</span>
                </div>
            </header>
            <div className="as2-metrics-page">
                <div className="metrics-header">
                    <h1>AS2 Transmission Metrics</h1>
                    <p className="subtitle">Insights into your AS2 data flow</p>
                </div>

                {/* Summary Cards */}
                <div className="metrics-grid summary">
                    <div className="metric-card">
                        <h3>Total Volume</h3>
                        <div className="value">{metrics.volume.reduce((acc, v) => acc + parseInt(v.count), 0)}</div>
                        <div className="label">Transmissions</div>
                    </div>
                    <div className="metric-card">
                        <h3>AS2 Inbound vs Outbound Success Rate</h3>
                        <div className="success-rates">
                            <div className="rate-item">
                                <span className="rate-label">Inbound:</span>
                                <span className="rate-value">
                                    {(() => {
                                        const inbound = metrics.status.filter(s => s.direction === 'INBOUND');
                                        const total = inbound.reduce((acc, s) => acc + parseInt(s.count), 0);
                                        const success = inbound.filter(s => ['SUCCESS', 'RECEIVED', 'COMPLETED'].includes(s.status))
                                            .reduce((acc, s) => acc + parseInt(s.count), 0);
                                        return total > 0 ? Math.round((success / total) * 100) : 0;
                                    })()}%
                                </span>
                            </div>
                            <div className="rate-item">
                                <span className="rate-label">Outbound:</span>
                                <span className="rate-value">
                                    {(() => {
                                        const outbound = metrics.status.filter(s => s.direction === 'OUTBOUND');
                                        const total = outbound.reduce((acc, s) => acc + parseInt(s.count), 0);
                                        const success = outbound.filter(s => ['SUCCESS', 'SENT', 'COMPLETED'].includes(s.status))
                                            .reduce((acc, s) => acc + parseInt(s.count), 0);
                                        return total > 0 ? Math.round((success / total) * 100) : 0;
                                    })()}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Grid */}
                <div className="metrics-grid charts">
                    <div className="chart-container main-trend">
                        <h3>Daily Transmission Volume</h3>
                        <div className="chart-wrapper">
                            <Line data={dailyData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="chart-container">
                        <h3>Inbound vs Outbound</h3>
                        <div className="chart-wrapper doughnut">
                            <Doughnut data={volumeData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="chart-container">
                        <h3>Status Distribution</h3>
                        <div className="chart-wrapper doughnut">
                            <Doughnut data={statusData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="chart-container">
                        <h3>Top 5 Senders</h3>
                        <div className="chart-wrapper">
                            <Bar data={topSendersData} options={{ indexAxis: 'y', maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="chart-container">
                        <h3>Top 5 Receivers</h3>
                        <div className="chart-wrapper">
                            <Bar data={topReceiversData} options={{ indexAxis: 'y', maintainAspectRatio: false }} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AS2Metrics;
